/* # Patient Dashboard v2.30.0
   - Added Fido2Enrollment integration in Security tab
   - Added Devices tab with Connected Devices card
   - Added My Safety and Today's Recommendations cards to Overview
     (answers "Am I safe?" / "What should I do next?" per dashboard
     optimisation brief - additive only, GeofenceManager unchanged)
   - Added floating Ask iVivaCore AI assistant (secondary entry point,
     not the default view)
*/
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
const { FiActivity, FiShield, FiLock, FiSettings, FiGrid, FiUser, FiWatch } =
  FiIcons;
import SafeIcon from "../common/SafeIcon";
import VitalsModule from "../components/dashboard/VitalsModule";
import ConnectedDevices from "../components/dashboard/ConnectedDevices";
import Card from "../components/shared/Card";
import { useAuth } from "../lib/auth";
import GeofenceManager from "../components/GeofenceManager";
import MFAEnrollment from "../components/MFAEnrollment";
import ConsentManager from "../components/ConsentManager";
import Fido2Enrollment from "../components/Fido2Enrollment";
import MySafetyCard from "../components/dashboard/MySafetyCard";
import RecommendationsCard from "../components/dashboard/RecommendationCard";
import AskIVivaCoreAI from "../components/dashboard/AskvivacoreReal";
import DemographicGate from "./DemographicGate";

export default function PatientDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const { user } = useAuth();

  const tabs = [
    { id: "overview", label: "Overview", icon: FiGrid },
    { id: "devices", label: "Devices", icon: FiWatch },
    { id: "security", label: "Security", icon: FiLock },
    { id: "privacy", label: "Privacy & Trust", icon: FiShield },
  ];

  return (
    <DemographicGate currentUser={user}>
      <div className="min-h-screen bg-slate-950 p-6 lg:p-10 text-white font-sans">
        <header className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-black tracking-tight">
              Health Command
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Patient ID: {user?.id?.slice(0, 8)} • v2.30.0
            </p>
          </div>
          <div className="flex bg-slate-900/50 p-1.5 rounded-2xl border border-slate-800">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-2 lg:px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-1 lg:gap-2 ${
                  activeTab === tab.id
                    ? "bg-primary-600 text-white shadow-lg shadow-primary-900/20"
                    : "text-slate-500 hover:text-white"
                }`}
              >
                <SafeIcon icon={tab.icon} /> {tab.label}
              </button>
            ))}
          </div>
        </header>

        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-10"
        >
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-10">
                <VitalsModule />
                <GeofenceManager patientId={user?.id} />
              </div>
              <div className="space-y-10">
                <div className="space-y-3">
                  <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    My Safety
                  </div>
                  <MySafetyCard />
                </div>
                <div className="space-y-3">
                  <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Recommendations
                  </div>
                  <RecommendationsCard />
                </div>
                <Card title="Quick Actions">
                  <div className="grid grid-cols-1 gap-4">
                    <button className="w-full bg-slate-900 hover:bg-slate-800 p-4 rounded-2xl text-left flex items-center justify-between group">
                      <span className="font-bold text-sm">
                        Download My Data
                      </span>
                      <SafeIcon
                        icon={FiIcons.FiChevronRight}
                        className="group-hover:translate-x-1 transition-transform"
                      />
                    </button>
                    <button className="w-full bg-rose-500/10 hover:bg-rose-500/20 p-4 rounded-2xl text-left flex items-center justify-between group border border-rose-500/20">
                      <span className="font-bold text-sm text-rose-500">
                        Trigger Alert
                      </span>
                      <SafeIcon
                        icon={FiIcons.FiAlertCircle}
                        className="text-rose-500"
                      />
                    </button>
                  </div>
                </Card>
              </div>
            </div>
          )}

          {activeTab === "devices" && (
            <div className="max-w-4xl mx-auto">
              <ConnectedDevices />
            </div>
          )}

          {activeTab === "security" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <MFAEnrollment />
              <Fido2Enrollment />
            </div>
          )}

          {activeTab === "privacy" && (
            <div className="max-w-4xl mx-auto">
              <ConsentManager />
            </div>
          )}
        </motion.div>

        <AskIVivaCoreAI role="patient" />
      </div>
    </DemographicGate>
  );
}
